// Pokemon Champions Competitive Teams Database
// Based on Reg M-A (April 8 - June 17, 2026) meta
// Sources: Pikalytics, Champions Lab, Serebii, Victory Road, Smogon

export const GAME_INFO = {
  name: 'Pokemon Champions',
  format: 'Regulation Set M-A',
  battleType: 'Doubles (VGC)',
  activePeriod: 'April 8 – June 17, 2026',
  rules: [
    'Double Battles only',
    'Select 4 of 6 Pokemon per match',
    'Auto-leveled to 50',
    'Species Clause (no duplicate Pokemon)',
    'Item Clause (no duplicate items)',
    'Mega Evolution allowed (1 per team)',
    'No Terastallization',
    'No IVs system (removed)',
    'No Paradox Pokemon',
    'No Treasures of Ruin',
    'No Koraidon/Miraidon',
  ],
};

export const teams = [
  {
    id: 'mega-froslass-hail',
    name: 'Mega Froslass Hail',
    archetype: 'Weather (Snow)',
    format: 'doubles',
    difficulty: 'Intermediate',
    description: 'The highest win-rate archetype at 63.8%. Mega Froslass summons Snow via Snow Warning on Mega Evolution, enabling 100% accurate Blizzard spread damage with 140 base SpA and 120 Speed.',
    strategy: `Lead Mega Froslass + a partner that benefits from hail or can redirect. Mega Evolve turn 1 to set Snow, then spam Blizzard which never misses. Snow also boosts Ice-type Defense by 50%. Farigiraf blocks priority with Armor Tail. Incineroar provides Fake Out + Intimidate. Milotic provides Competitive to punish Intimidate users. Kingambit closes games with Supreme Overlord.`,
    leadRecommendations: [
      { pokemon: ['Froslass', 'Incineroar'], situation: 'Default — Fake Out + Mega Evolve Froslass safely' },
      { pokemon: ['Froslass', 'Farigiraf'], situation: 'Against priority users — Armor Tail blocks Fake Out' },
      { pokemon: ['Incineroar', 'Milotic'], situation: 'Against Intimidate spam — Competitive punishes' },
    ],
    backlineNotes: 'Kingambit is the endgame closer once Froslass has weakened the board. Farigiraf can set Trick Room if needed against hyper-offense.',
    pokemon: [
      {
        name: 'Froslass',
        types: ['Ice', 'Ghost'],
        ability: 'Cursed Body',
        nature: 'Timid',
        statPoints: { hp: 2, atk: 0, def: 0, spa: 32, spd: 0, spe: 32 },
        item: 'Frosslasite',
        moves: ['Blizzard', 'Shadow Ball', 'Icy Wind', 'Protect'],
        role: 'Mega Evolution / Special Sweeper',
        notes: 'Mega Froslass: 140 SpA, 120 Spe, Snow Warning. 63.8% win rate — best Mega in the game. Blizzard is 100% accurate in Snow and hits both opponents. Icy Wind for speed control.',
        baseStats: { hp: 70, atk: 80, def: 70, spa: 80, spd: 80, spe: 110 },
        megaStats: { hp: 70, atk: 80, def: 80, spa: 140, spd: 90, spe: 120 },
      },
      {
        name: 'Incineroar',
        types: ['Fire', 'Dark'],
        ability: 'Intimidate',
        nature: 'Careful',
        statPoints: { hp: 32, atk: 2, def: 0, spa: 0, spd: 32, spe: 0 },
        item: 'Sitrus Berry',
        moves: ['Fake Out', 'Flare Blitz', 'Parting Shot', 'Darkest Lariat'],
        role: 'Support / Pivot',
        notes: '#1 usage at 62.4% tournament rate. Fake Out protects Froslass during Mega Evolution. Intimidate + Parting Shot cycles weaken physical threats.',
        baseStats: { hp: 95, atk: 115, def: 90, spa: 80, spd: 90, spe: 60 },
      },
      {
        name: 'Farigiraf',
        types: ['Normal', 'Psychic'],
        ability: 'Armor Tail',
        nature: 'Relaxed',
        statPoints: { hp: 32, atk: 0, def: 32, spa: 0, spd: 2, spe: 0 },
        item: 'Mental Herb',
        moves: ['Trick Room', 'Psychic', 'Helping Hand', 'Protect'],
        role: 'Trick Room / Priority Blocker',
        notes: 'Armor Tail blocks ALL priority moves (Fake Out, Extreme Speed, Grassy Glide). Mental Herb prevents Taunt. Trick Room gives a secondary speed mode.',
        baseStats: { hp: 120, atk: 90, def: 70, spa: 110, spd: 70, spe: 60 },
      },
      {
        name: 'Milotic',
        types: ['Water'],
        ability: 'Competitive',
        nature: 'Bold',
        statPoints: { hp: 32, atk: 0, def: 32, spa: 2, spd: 0, spe: 0 },
        item: 'Leftovers',
        moves: ['Scald', 'Ice Beam', 'Recover', 'Protect'],
        role: 'Bulky Water / Intimidate Punish',
        notes: '#3 ELO rating (9,298). Competitive gives +2 SpA when any stat is lowered — hard-counters Intimidate spam. Scald burn chance cripples physical attackers.',
        baseStats: { hp: 95, atk: 60, def: 79, spa: 100, spd: 125, spe: 81 },
      },
      {
        name: 'Kingambit',
        types: ['Dark', 'Steel'],
        ability: 'Supreme Overlord',
        nature: 'Adamant',
        statPoints: { hp: 32, atk: 32, def: 0, spa: 0, spd: 2, spe: 0 },
        item: 'Focus Sash',
        moves: ['Kowtow Cleave', 'Iron Head', 'Sucker Punch', 'Swords Dance'],
        role: 'Late-game Wincon',
        notes: '35.7% tournament usage, 52.1% WR. Supreme Overlord powers up as allies faint. Focus Sash (key item in Champions since no Assault Vest/Life Orb). Dark/Steel great defensive typing.',
        baseStats: { hp: 100, atk: 135, def: 120, spa: 60, spd: 85, spe: 50 },
      },
      {
        name: 'Garchomp',
        types: ['Dragon', 'Ground'],
        ability: 'Rough Skin',
        nature: 'Jolly',
        statPoints: { hp: 2, atk: 32, def: 0, spa: 0, spd: 0, spe: 32 },
        item: 'Focus Sash',
        moves: ['Earthquake', 'Dragon Claw', 'Rock Slide', 'Protect'],
        role: 'Physical Attacker',
        notes: '24.1% tournament usage. Earthquake hits both opponents in doubles. Rock Slide flinch chance. Rough Skin punishes contact moves.',
        baseStats: { hp: 108, atk: 130, def: 95, spa: 80, spd: 85, spe: 102 },
      },
    ],
  },
  {
    id: 'mega-blastoise-rain', format: 'doubles',
    name: 'Mega Blastoise Rain',
    archetype: 'Weather (Rain)',
    difficulty: 'Intermediate',
    description: 'Rain archetype centered around Mega Blastoise. Part of the strongest team cores (73.4% WR with Heat Rotom and Tsareena). Politoed sets rain for boosted Water moves.',
    strategy: `Set rain with Politoed, then Mega Evolve Blastoise for powerful rain-boosted Water attacks. Heat Rotom provides Electric coverage and has the highest individual win rate (54.6%). Tsareena offers Queenly Majesty to block priority. Dragapult provides fast Ghost/Dragon coverage. Incineroar for pivoting.`,
    leadRecommendations: [
      { pokemon: ['Politoed', 'Blastoise'], situation: 'Default — Rain + Mega Blastoise sweep' },
      { pokemon: ['Incineroar', 'Heat Rotom'], situation: 'Against weather wars — Fake Out + immediate KOs' },
      { pokemon: ['Tsareena', 'Blastoise'], situation: 'Against priority users — Queenly Majesty protection' },
    ],
    backlineNotes: 'Dragapult is a fast closer. Heat Rotom checks bulky waters and grasses. Tsareena provides priority block and Grass coverage.',
    pokemon: [
      {
        name: 'Blastoise',
        types: ['Water'],
        ability: 'Torrent',
        nature: 'Modest',
        statPoints: { hp: 32, atk: 0, def: 2, spa: 32, spd: 0, spe: 0 },
        item: 'Blastoisinite',
        moves: ['Water Pulse', 'Dark Pulse', 'Aura Sphere', 'Protect'],
        role: 'Mega Evolution / Special Attacker',
        notes: '60.8% WR archetype. Mega Launcher boosts pulse/aura moves by 50%. Water Pulse + rain is devastating. Dark Pulse and Aura Sphere get Mega Launcher boost too.',
        baseStats: { hp: 79, atk: 83, def: 100, spa: 85, spd: 105, spe: 78 },
        megaStats: { hp: 79, atk: 103, def: 120, spa: 135, spd: 115, spe: 78 },
      },
      {
        name: 'Politoed',
        types: ['Water'],
        ability: 'Drizzle',
        nature: 'Bold',
        statPoints: { hp: 32, atk: 0, def: 32, spa: 0, spd: 2, spe: 0 },
        item: 'Sitrus Berry',
        moves: ['Scald', 'Icy Wind', 'Helping Hand', 'Protect'],
        role: 'Rain Setter / Support',
        notes: 'Drizzle sets rain on entry. Icy Wind speed control. Helping Hand boosts Blastoise damage. Reliable rain reset on switch-in.',
        baseStats: { hp: 90, atk: 75, def: 75, spa: 90, spd: 100, spe: 70 },
      },
      {
        name: 'Rotom-Heat',
        types: ['Electric', 'Fire'],
        ability: 'Levitate',
        nature: 'Modest',
        statPoints: { hp: 32, atk: 0, def: 0, spa: 32, spd: 2, spe: 0 },
        item: 'Choice Scarf',
        moves: ['Overheat', 'Thunderbolt', 'Volt Switch', 'Will-O-Wisp'],
        role: 'Special Attacker / Pivot',
        notes: '#2 ELO rating (9,309), 54.6% WR. Levitate dodges Earthquake. Part of 73.4% WR core with Blastoise. Choice Scarf for speed. Volt Switch pivots.',
        baseStats: { hp: 50, atk: 65, def: 107, spa: 105, spd: 107, spe: 86 },
      },
      {
        name: 'Tsareena',
        types: ['Grass'],
        ability: 'Queenly Majesty',
        nature: 'Jolly',
        statPoints: { hp: 2, atk: 32, def: 0, spa: 0, spd: 0, spe: 32 },
        item: 'Focus Sash',
        moves: ['Power Whip', 'High Jump Kick', 'U-turn', 'Protect'],
        role: 'Priority Blocker / Physical Attacker',
        notes: 'Part of 73.4% WR core. Queenly Majesty blocks ALL priority moves. Power Whip hits Water types. High Jump Kick for Steel types.',
        baseStats: { hp: 72, atk: 120, def: 98, spa: 50, spd: 98, spe: 72 },
      },
      {
        name: 'Dragapult',
        types: ['Dragon', 'Ghost'],
        ability: 'Clear Body',
        nature: 'Timid',
        statPoints: { hp: 2, atk: 0, def: 0, spa: 32, spd: 0, spe: 32 },
        item: 'Focus Sash',
        moves: ['Shadow Ball', 'Draco Meteor', 'Thunderbolt', 'Protect'],
        role: 'Fast Special Attacker',
        notes: '28.3% tournament usage. Fastest relevant attacker in the format. Clear Body prevents Intimidate and other stat drops. Ghost/Dragon STAB excellent coverage.',
        baseStats: { hp: 88, atk: 120, def: 75, spa: 100, spd: 75, spe: 142 },
      },
      {
        name: 'Incineroar',
        types: ['Fire', 'Dark'],
        ability: 'Intimidate',
        nature: 'Careful',
        statPoints: { hp: 32, atk: 2, def: 0, spa: 0, spd: 32, spe: 0 },
        item: 'Leftovers',
        moves: ['Fake Out', 'Flare Blitz', 'Parting Shot', 'Darkest Lariat'],
        role: 'Support / Pivot',
        notes: 'Standard Incineroar. Leftovers since Sitrus is on Politoed (Item Clause). Still the most-used Pokemon in the format.',
        baseStats: { hp: 95, atk: 115, def: 90, spa: 80, spd: 90, spe: 60 },
      },
    ],
  },
  {
    id: 'hard-trick-room', format: 'doubles',
    name: 'Hard Trick Room',
    archetype: 'Trick Room',
    difficulty: 'Advanced',
    description: '62.3% win rate archetype. Fully commits to reversing speed with multiple TR setters and the slowest, hardest-hitting Pokemon in the format.',
    strategy: `Set Trick Room as soon as possible with Farigiraf (Armor Tail blocks Fake Out) or Hatterene (Magic Bounce reflects Taunt). Once TR is up, your slow powerhouses dominate — Mega Crabominable hits like a truck with Iron Fist-boosted Ice Hammer. Torkoal provides sun for coverage and is extremely slow = extremely fast in TR. Incineroar pivots with Parting Shot.`,
    leadRecommendations: [
      { pokemon: ['Farigiraf', 'Incineroar'], situation: 'Default — Armor Tail + Fake Out protects TR setup' },
      { pokemon: ['Hatterene', 'Sinistcha'], situation: 'Against Taunt leads — Magic Bounce + Rage Powder' },
      { pokemon: ['Torkoal', 'Farigiraf'], situation: 'Against passive teams — immediate pressure + TR' },
    ],
    backlineNotes: 'Mega Crabominable and Torkoal are devastating in TR. Keep them in the back until TR is active.',
    pokemon: [
      {
        name: 'Farigiraf',
        types: ['Normal', 'Psychic'],
        ability: 'Armor Tail',
        nature: 'Relaxed',
        statPoints: { hp: 32, atk: 0, def: 32, spa: 2, spd: 0, spe: 0 },
        ivs: { spe: 0 },
        item: 'Mental Herb',
        moves: ['Trick Room', 'Psychic', 'Helping Hand', 'Protect'],
        role: 'Primary TR Setter',
        notes: 'Armor Tail blocks Fake Out from disrupting Trick Room. Mental Herb prevents Taunt. Min Speed to move first under TR.',
        baseStats: { hp: 120, atk: 90, def: 70, spa: 110, spd: 70, spe: 60 },
      },
      {
        name: 'Hatterene',
        types: ['Psychic', 'Fairy'],
        ability: 'Magic Bounce',
        nature: 'Quiet',
        statPoints: { hp: 32, atk: 0, def: 2, spa: 32, spd: 0, spe: 0 },
        ivs: { spe: 0 },
        item: 'Focus Sash',
        moves: ['Trick Room', 'Dazzling Gleam', 'Psychic', 'Mystical Fire'],
        role: 'Backup TR Setter / Attacker',
        notes: 'Magic Bounce reflects Taunt, Spore, Will-O-Wisp back at user. 29 base Speed = ideal for TR. Focus Sash guarantees survival for TR setup.',
        baseStats: { hp: 57, atk: 90, def: 95, spa: 136, spd: 103, spe: 29 },
      },
      {
        name: 'Crabominable',
        types: ['Fighting', 'Ice'],
        ability: 'Iron Fist',
        nature: 'Brave',
        statPoints: { hp: 32, atk: 32, def: 2, spa: 0, spd: 0, spe: 0 },
        ivs: { spe: 0 },
        item: 'Crabominite',
        moves: ['Ice Hammer', 'Close Combat', 'Earthquake', 'Protect'],
        role: 'Mega / TR Sweeper',
        notes: 'Mega Crabominable gets Iron Fist boosting Ice Hammer and other punch moves. Extremely slow = moves first in TR. Fighting/Ice coverage is brutal.',
        baseStats: { hp: 97, atk: 132, def: 77, spa: 62, spd: 67, spe: 43 },
      },
      {
        name: 'Torkoal',
        types: ['Fire'],
        ability: 'Drought',
        nature: 'Quiet',
        statPoints: { hp: 32, atk: 0, def: 2, spa: 32, spd: 0, spe: 0 },
        ivs: { spe: 0 },
        item: 'Charcoal',
        moves: ['Eruption', 'Heat Wave', 'Earth Power', 'Protect'],
        role: 'TR Attacker / Sun Setter',
        notes: 'Eruption in Sun under TR at full HP is one of the strongest attacks in the game. 20 base Speed = fastest thing in TR.',
        baseStats: { hp: 70, atk: 85, def: 140, spa: 85, spd: 70, spe: 20 },
      },
      {
        name: 'Sinistcha',
        types: ['Grass', 'Ghost'],
        ability: 'Hospitality',
        nature: 'Quiet',
        statPoints: { hp: 32, atk: 0, def: 14, spa: 20, spd: 0, spe: 0 },
        ivs: { spe: 0 },
        item: 'Sitrus Berry',
        moves: ['Matcha Gotcha', 'Rage Powder', 'Trick Room', 'Life Dew'],
        role: 'Redirector / Third TR Setter / Healer',
        notes: 'Hospitality heals partner on entry. Rage Powder redirects attacks. Third TR setter ensures it always goes up. Life Dew heals both allies.',
        baseStats: { hp: 71, atk: 60, def: 106, spa: 121, spd: 80, spe: 70 },
      },
      {
        name: 'Incineroar',
        types: ['Fire', 'Dark'],
        ability: 'Intimidate',
        nature: 'Brave',
        statPoints: { hp: 32, atk: 32, def: 2, spa: 0, spd: 0, spe: 0 },
        ivs: { spe: 0 },
        item: 'Leftovers',
        moves: ['Fake Out', 'Flare Blitz', 'Parting Shot', 'Darkest Lariat'],
        role: 'Support / Pivot',
        notes: 'Min Speed Incineroar for TR. Fake Out buys a turn for TR setup. Parting Shot pivots while lowering threats.',
        baseStats: { hp: 95, atk: 115, def: 90, spa: 80, spd: 90, spe: 60 },
      },
    ],
  },
  {
    id: 'mega-garchomp-offense', format: 'doubles',
    name: 'Mega Garchomp Offense',
    archetype: 'Hyper Offense',
    difficulty: 'Beginner',
    description: '55.2% win rate with 24.1% tournament usage. Mega Garchomp is one of the most popular and powerful Megas available. Sand Force in sand boosts Ground, Rock, and Steel moves by 30%.',
    strategy: `Lead aggressively with Whimsicott for Prankster Tailwind then bring in Mega Garchomp to outspeed and destroy everything. Tyranitar sets Sand which boosts Mega Garchomp via Sand Force. Excadrill provides a secondary Sand Rush sweeper. Incineroar and Dragapult round out coverage.`,
    leadRecommendations: [
      { pokemon: ['Whimsicott', 'Garchomp'], situation: 'Default — Tailwind + immediate Mega pressure' },
      { pokemon: ['Tyranitar', 'Excadrill'], situation: 'Against Trick Room — Sand Rush outspeeds in sand' },
      { pokemon: ['Incineroar', 'Dragapult'], situation: 'Against offensive leads — Fake Out + fast KOs' },
    ],
    backlineNotes: 'Tyranitar + Excadrill as a backup sand core. Dragapult for late-game speed.',
    pokemon: [
      {
        name: 'Garchomp',
        types: ['Dragon', 'Ground'],
        ability: 'Rough Skin',
        nature: 'Jolly',
        statPoints: { hp: 2, atk: 32, def: 0, spa: 0, spd: 0, spe: 32 },
        item: 'Garchompite',
        moves: ['Earthquake', 'Dragon Claw', 'Rock Slide', 'Protect'],
        role: 'Mega / Physical Sweeper',
        notes: '55.2% WR, top Mega by tournament usage. Sand Force boosts Ground/Rock/Steel moves 30% in sand. Earthquake + Rock Slide spread damage devastates doubles.',
        baseStats: { hp: 108, atk: 130, def: 95, spa: 80, spd: 85, spe: 102 },
        megaStats: { hp: 108, atk: 170, def: 115, spa: 120, spd: 95, spe: 92 },
      },
      {
        name: 'Whimsicott',
        types: ['Grass', 'Fairy'],
        ability: 'Prankster',
        nature: 'Timid',
        statPoints: { hp: 32, atk: 0, def: 2, spa: 0, spd: 0, spe: 32 },
        item: 'Focus Sash',
        moves: ['Tailwind', 'Encore', 'Moonblast', 'Protect'],
        role: 'Speed Control / Disruptor',
        notes: '22.1% tournament usage. Prankster Tailwind goes first always. Encore locks opponents into bad moves. Moonblast hits Dragons.',
        baseStats: { hp: 60, atk: 67, def: 85, spa: 77, spd: 75, spe: 116 },
      },
      {
        name: 'Tyranitar',
        types: ['Rock', 'Dark'],
        ability: 'Sand Stream',
        nature: 'Adamant',
        statPoints: { hp: 32, atk: 32, def: 0, spa: 0, spd: 2, spe: 0 },
        item: 'Leftovers',
        moves: ['Rock Slide', 'Crunch', 'Ice Punch', 'Protect'],
        role: 'Sand Setter / Bulky Attacker',
        notes: 'Sand Stream sets sand for Mega Garchomp Sand Force. SpD boosted 50% in sand. Rock Slide for doubles flinch.',
        baseStats: { hp: 100, atk: 134, def: 110, spa: 95, spd: 100, spe: 61 },
      },
      {
        name: 'Excadrill',
        types: ['Ground', 'Steel'],
        ability: 'Sand Rush',
        nature: 'Jolly',
        statPoints: { hp: 2, atk: 32, def: 0, spa: 0, spd: 0, spe: 32 },
        item: 'Focus Sash',
        moves: ['Earthquake', 'Iron Head', 'Rock Slide', 'Protect'],
        role: 'Sand Sweeper',
        notes: 'Sand Rush doubles Speed in sand. Secondary Ground attacker alongside Garchomp. Ground/Steel typing gives great resistances.',
        baseStats: { hp: 110, atk: 135, def: 60, spa: 50, spd: 65, spe: 88 },
      },
      {
        name: 'Incineroar',
        types: ['Fire', 'Dark'],
        ability: 'Intimidate',
        nature: 'Careful',
        statPoints: { hp: 32, atk: 2, def: 0, spa: 0, spd: 32, spe: 0 },
        item: 'Sitrus Berry',
        moves: ['Fake Out', 'Flare Blitz', 'Parting Shot', 'Darkest Lariat'],
        role: 'Support / Pivot',
        notes: 'Standard Incineroar. Fake Out + Intimidate + Parting Shot. The glue of every team in Champions.',
        baseStats: { hp: 95, atk: 115, def: 90, spa: 80, spd: 90, spe: 60 },
      },
      {
        name: 'Dragapult',
        types: ['Dragon', 'Ghost'],
        ability: 'Clear Body',
        nature: 'Timid',
        statPoints: { hp: 2, atk: 0, def: 0, spa: 32, spd: 0, spe: 32 },
        item: 'Choice Scarf',
        moves: ['Shadow Ball', 'Draco Meteor', 'Thunderbolt', 'U-turn'],
        role: 'Fast Special Attacker / Pivot',
        notes: '28.3% usage. Fastest Pokemon in the format. Clear Body ignores Intimidate. Choice Scarf makes it impossible to outspeed.',
        baseStats: { hp: 88, atk: 120, def: 75, spa: 100, spd: 75, spe: 142 },
      },
    ],
  },
  {
    id: 'mega-meganium-sun', format: 'doubles',
    name: 'Mega Meganium Sun',
    archetype: 'Weather (Sun)',
    difficulty: 'Advanced',
    description: '#1 ELO rating (9,315). Mega Meganium has the unique ability Mega Sol which makes it always act as if Sun is active — boosting its Grass moves and enabling Sun-synergy without a weather setter.',
    strategy: `Mega Meganium acts as if Sun is always active thanks to Mega Sol, meaning Solar Beam requires no charge and Fire moves on teammates are boosted when paired with an actual Drought setter. Torkoal provides real Sun for the rest of the team. Charizard thrives in Sun with boosted Fire moves. Arcanine provides Intimidate and a secondary Fire STAB. Kingambit and Whimsicott complete coverage.`,
    leadRecommendations: [
      { pokemon: ['Meganium', 'Torkoal'], situation: 'Default — Double Sun + immediate offensive pressure' },
      { pokemon: ['Whimsicott', 'Charizard'], situation: 'Against slow teams — Tailwind + Sun sweeping' },
      { pokemon: ['Incineroar', 'Meganium'], situation: 'Against offensive leads — Fake Out + Mega setup' },
    ],
    backlineNotes: 'Charizard and Kingambit clean up. Torkoal can come back in to reset Sun if needed.',
    pokemon: [
      {
        name: 'Meganium',
        types: ['Grass'],
        ability: 'Overgrow',
        nature: 'Modest',
        statPoints: { hp: 32, atk: 0, def: 2, spa: 32, spd: 0, spe: 0 },
        item: 'Meganiumite',
        moves: ['Giga Drain', 'Ancient Power', 'Leech Seed', 'Protect'],
        role: 'Mega / Bulky Special Attacker',
        notes: '#1 ELO (9,315), 52.2% WR. Mega Sol = permanent Sun for itself. No need for weather setter to function. Giga Drain for sustain.',
        baseStats: { hp: 80, atk: 82, def: 100, spa: 83, spd: 100, spe: 80 },
      },
      {
        name: 'Torkoal',
        types: ['Fire'],
        ability: 'Drought',
        nature: 'Quiet',
        statPoints: { hp: 32, atk: 0, def: 2, spa: 32, spd: 0, spe: 0 },
        ivs: { spe: 0 },
        item: 'Charcoal',
        moves: ['Eruption', 'Heat Wave', 'Earth Power', 'Protect'],
        role: 'Sun Setter / Attacker',
        notes: 'Drought sets real Sun for the whole team. Sun-boosted Eruption at full HP is devastating.',
        baseStats: { hp: 70, atk: 85, def: 140, spa: 85, spd: 70, spe: 20 },
      },
      {
        name: 'Charizard',
        types: ['Fire', 'Flying'],
        ability: 'Solar Power',
        nature: 'Timid',
        statPoints: { hp: 2, atk: 0, def: 0, spa: 32, spd: 0, spe: 32 },
        item: 'Focus Sash',
        moves: ['Heat Wave', 'Air Slash', 'Solar Beam', 'Protect'],
        role: 'Sun Sweeper',
        notes: 'Solar Power gives 1.5x SpA in Sun. Solar Beam fires instantly in Sun. Heat Wave spread damage in Sun is brutal.',
        baseStats: { hp: 78, atk: 84, def: 78, spa: 109, spd: 85, spe: 100 },
      },
      {
        name: 'Arcanine',
        types: ['Fire'],
        ability: 'Intimidate',
        nature: 'Adamant',
        statPoints: { hp: 32, atk: 32, def: 0, spa: 0, spd: 2, spe: 0 },
        item: 'Sitrus Berry',
        moves: ['Flare Blitz', 'Wild Charge', 'Extreme Speed', 'Protect'],
        role: 'Physical Attacker / Intimidate',
        notes: 'Second Intimidate user for cycling. Extreme Speed is +2 priority for cleanup. Sun-boosted Flare Blitz is massive damage.',
        baseStats: { hp: 90, atk: 110, def: 80, spa: 100, spd: 80, spe: 95 },
      },
      {
        name: 'Whimsicott',
        types: ['Grass', 'Fairy'],
        ability: 'Prankster',
        nature: 'Timid',
        statPoints: { hp: 32, atk: 0, def: 2, spa: 0, spd: 0, spe: 32 },
        item: 'Focus Sash',
        moves: ['Tailwind', 'Encore', 'Moonblast', 'Helping Hand'],
        role: 'Speed Control / Support',
        notes: 'Prankster Tailwind and Helping Hand for boosted sweeping turns. Encore locks threats.',
        baseStats: { hp: 60, atk: 67, def: 85, spa: 77, spd: 75, spe: 116 },
      },
      {
        name: 'Kingambit',
        types: ['Dark', 'Steel'],
        ability: 'Supreme Overlord',
        nature: 'Adamant',
        statPoints: { hp: 32, atk: 32, def: 0, spa: 0, spd: 2, spe: 0 },
        item: 'Leftovers',
        moves: ['Kowtow Cleave', 'Iron Head', 'Sucker Punch', 'Swords Dance'],
        role: 'Late-game Wincon',
        notes: 'Supreme Overlord endgame threat. Dark/Steel resists most attacks. Sucker Punch priority catches faster threats.',
        baseStats: { hp: 100, atk: 135, def: 120, spa: 60, spd: 85, spe: 50 },
      },
    ],
  },
  {
    id: 'mega-scizor-balance', format: 'doubles',
    name: 'Mega Scizor Balance',
    archetype: 'Balance',
    difficulty: 'Beginner',
    description: 'Mega Scizor (51.8% WR, #5 ELO) is the most-played Mega. Balance approach with Technician-boosted Bullet Punch priority and strong defensive typing.',
    strategy: `Mega Scizor provides consistent priority damage with Technician Bullet Punch, great defensive typing (Bug/Steel), and pivoting with U-turn. Chesnaught (52.8% WR, #4 ELO) provides Bulletproof to ignore many special attacks and Spiky Shield. Milotic punishes Intimidate with Competitive. Dragapult provides speed. Incineroar is the team glue.`,
    leadRecommendations: [
      { pokemon: ['Incineroar', 'Scizor'], situation: 'Default — Fake Out + safe Mega Evolution' },
      { pokemon: ['Chesnaught', 'Milotic'], situation: 'Against physical teams — Spiky Shield + Competitive' },
      { pokemon: ['Dragapult', 'Scizor'], situation: 'Against slower teams — speed + priority pressure' },
    ],
    backlineNotes: 'Flexible backline. Chesnaught walls physical threats. Milotic handles Intimidate matchups. Garchomp for cleanup.',
    pokemon: [
      {
        name: 'Scizor',
        types: ['Bug', 'Steel'],
        ability: 'Technician',
        nature: 'Adamant',
        statPoints: { hp: 32, atk: 32, def: 2, spa: 0, spd: 0, spe: 0 },
        item: 'Scizorite',
        moves: ['Bullet Punch', 'Bug Bite', 'Swords Dance', 'Protect'],
        role: 'Mega / Priority Sweeper',
        notes: '#5 ELO, 51.8% WR, most-played Mega. Technician boosts Bullet Punch (60 BP) and Bug Bite (90 BP after Technician). Swords Dance + Bullet Punch sweeps endgame.',
        baseStats: { hp: 70, atk: 130, def: 100, spa: 55, spd: 80, spe: 65 },
        megaStats: { hp: 70, atk: 150, def: 140, spa: 65, spd: 100, spe: 75 },
      },
      {
        name: 'Chesnaught',
        types: ['Grass', 'Fighting'],
        ability: 'Bulletproof',
        nature: 'Impish',
        statPoints: { hp: 32, atk: 2, def: 32, spa: 0, spd: 0, spe: 0 },
        item: 'Leftovers',
        moves: ['Spiky Shield', 'Wood Hammer', 'Drain Punch', 'Leech Seed'],
        role: 'Physical Wall',
        notes: '#4 ELO, 52.8% WR. Bulletproof blocks Shadow Ball, Aura Sphere, Focus Blast, Energy Ball, etc. Spiky Shield is Protect that damages contact attackers.',
        baseStats: { hp: 88, atk: 107, def: 122, spa: 74, spd: 75, spe: 64 },
      },
      {
        name: 'Milotic',
        types: ['Water'],
        ability: 'Competitive',
        nature: 'Bold',
        statPoints: { hp: 32, atk: 0, def: 32, spa: 2, spd: 0, spe: 0 },
        item: 'Sitrus Berry',
        moves: ['Scald', 'Ice Beam', 'Recover', 'Protect'],
        role: 'Bulky Water / Intimidate Punish',
        notes: 'Competitive +2 SpA when Intimidated. Scald burns physical attackers. Recover for sustain. Top 3 ELO Pokemon.',
        baseStats: { hp: 95, atk: 60, def: 79, spa: 100, spd: 125, spe: 81 },
      },
      {
        name: 'Dragapult',
        types: ['Dragon', 'Ghost'],
        ability: 'Clear Body',
        nature: 'Timid',
        statPoints: { hp: 2, atk: 0, def: 0, spa: 32, spd: 0, spe: 32 },
        item: 'Focus Sash',
        moves: ['Shadow Ball', 'Draco Meteor', 'Thunderbolt', 'Protect'],
        role: 'Fast Attacker',
        notes: 'Fastest Pokemon in format. Clear Body ignores Intimidate.',
        baseStats: { hp: 88, atk: 120, def: 75, spa: 100, spd: 75, spe: 142 },
      },
      {
        name: 'Incineroar',
        types: ['Fire', 'Dark'],
        ability: 'Intimidate',
        nature: 'Careful',
        statPoints: { hp: 32, atk: 2, def: 0, spa: 0, spd: 32, spe: 0 },
        item: 'Focus Sash',
        moves: ['Fake Out', 'Flare Blitz', 'Parting Shot', 'Darkest Lariat'],
        role: 'Support / Pivot',
        notes: 'Standard Incineroar. Fake Out + Intimidate + Parting Shot. Focus Sash since other items taken.',
        baseStats: { hp: 95, atk: 115, def: 90, spa: 80, spd: 90, spe: 60 },
      },
      {
        name: 'Garchomp',
        types: ['Dragon', 'Ground'],
        ability: 'Rough Skin',
        nature: 'Jolly',
        statPoints: { hp: 2, atk: 32, def: 0, spa: 0, spd: 0, spe: 32 },
        item: 'Choice Scarf',
        moves: ['Earthquake', 'Dragon Claw', 'Rock Slide', 'Poison Jab'],
        role: 'Revenge Killer',
        notes: 'Choice Scarf for speed control. Earthquake spread damage. Poison Jab for Fairy types. Rough Skin chip on contact.',
        baseStats: { hp: 108, atk: 130, def: 95, spa: 80, spd: 85, spe: 102 },
      },
    ],
  },
  {
    id: 'strong-cores', format: 'doubles',
    name: 'Heat Rotom + Victreebel Core',
    archetype: 'Core Offense',
    difficulty: 'Intermediate',
    description: 'Built around one of the strongest cores (73.4% WR): Heat Rotom + Victreebel. Chlorophyll Victreebel in Sun outspeeds everything and puts threats to sleep.',
    strategy: `Set Sun with Torkoal or abuse Mega Victreebel. Heat Rotom provides Electric/Fire coverage with Levitate immunity to Earthquake. Victreebel under Sun doubles Speed via Chlorophyll and threatens Sleep Powder. Mega Victreebel gets Innards Out for chip damage when KOd. Incineroar and Garchomp provide standard support.`,
    leadRecommendations: [
      { pokemon: ['Torkoal', 'Victreebel'], situation: 'Default — Sun + Chlorophyll Sleep Powder' },
      { pokemon: ['Rotom-Heat', 'Incineroar'], situation: 'Against rain teams — Fake Out + Electric coverage' },
      { pokemon: ['Garchomp', 'Rotom-Heat'], situation: 'Against sand — Ground immunity + coverage' },
    ],
    backlineNotes: 'Kingambit endgame closer. Victreebel can go to back if Sleep Powder not needed turn 1.',
    pokemon: [
      {
        name: 'Victreebel',
        types: ['Grass', 'Poison'],
        ability: 'Chlorophyll',
        nature: 'Modest',
        statPoints: { hp: 2, atk: 0, def: 0, spa: 32, spd: 0, spe: 32 },
        item: 'Victreebelite',
        moves: ['Sleep Powder', 'Giga Drain', 'Sludge Bomb', 'Weather Ball'],
        role: 'Mega / Sun Sweeper',
        notes: 'Mega Victreebel gets Innards Out. Chlorophyll doubles Speed in Sun before Mega. Sleep Powder is game-changing in doubles. Part of 73.4% WR core.',
        baseStats: { hp: 80, atk: 105, def: 65, spa: 100, spd: 70, spe: 70 },
      },
      {
        name: 'Rotom-Heat',
        types: ['Electric', 'Fire'],
        ability: 'Levitate',
        nature: 'Modest',
        statPoints: { hp: 32, atk: 0, def: 0, spa: 32, spd: 2, spe: 0 },
        item: 'Choice Scarf',
        moves: ['Overheat', 'Thunderbolt', 'Volt Switch', 'Will-O-Wisp'],
        role: 'Special Attacker / Pivot',
        notes: '#2 ELO (9,309), 54.6% WR. Levitate dodges Earthquake. Part of 73.4% WR core. Scarf for speed.',
        baseStats: { hp: 50, atk: 65, def: 107, spa: 105, spd: 107, spe: 86 },
      },
      {
        name: 'Torkoal',
        types: ['Fire'],
        ability: 'Drought',
        nature: 'Quiet',
        statPoints: { hp: 32, atk: 0, def: 2, spa: 32, spd: 0, spe: 0 },
        ivs: { spe: 0 },
        item: 'Charcoal',
        moves: ['Eruption', 'Heat Wave', 'Earth Power', 'Protect'],
        role: 'Sun Setter',
        notes: 'Drought for Victreebel Chlorophyll. Eruption in Sun is devastating.',
        baseStats: { hp: 70, atk: 85, def: 140, spa: 85, spd: 70, spe: 20 },
      },
      {
        name: 'Incineroar',
        types: ['Fire', 'Dark'],
        ability: 'Intimidate',
        nature: 'Careful',
        statPoints: { hp: 32, atk: 2, def: 0, spa: 0, spd: 32, spe: 0 },
        item: 'Sitrus Berry',
        moves: ['Fake Out', 'Flare Blitz', 'Parting Shot', 'Darkest Lariat'],
        role: 'Support / Pivot',
        notes: 'Standard Incineroar pivot and disruptor.',
        baseStats: { hp: 95, atk: 115, def: 90, spa: 80, spd: 90, spe: 60 },
      },
      {
        name: 'Garchomp',
        types: ['Dragon', 'Ground'],
        ability: 'Rough Skin',
        nature: 'Jolly',
        statPoints: { hp: 2, atk: 32, def: 0, spa: 0, spd: 0, spe: 32 },
        item: 'Focus Sash',
        moves: ['Earthquake', 'Dragon Claw', 'Rock Slide', 'Protect'],
        role: 'Physical Attacker',
        notes: 'Earthquake + Rock Slide spread damage. Rough Skin punishes contact.',
        baseStats: { hp: 108, atk: 130, def: 95, spa: 80, spd: 85, spe: 102 },
      },
      {
        name: 'Kingambit',
        types: ['Dark', 'Steel'],
        ability: 'Supreme Overlord',
        nature: 'Adamant',
        statPoints: { hp: 32, atk: 32, def: 0, spa: 0, spd: 2, spe: 0 },
        item: 'Leftovers',
        moves: ['Kowtow Cleave', 'Iron Head', 'Sucker Punch', 'Swords Dance'],
        role: 'Late-game Wincon',
        notes: 'Supreme Overlord endgame. Dark/Steel typing is premium.',
        baseStats: { hp: 100, atk: 135, def: 120, spa: 60, spd: 85, spe: 50 },
      },
    ],
  },
  // ============================================================
  // SINGLES TEAMS
  // ============================================================
  {
    id: 'singles-dragon-offense', format: 'singles',
    name: 'Dragon Scale Offense',
    archetype: 'Hyper Offense',
    difficulty: 'Intermediate',
    description: 'A fast, aggressive singles team built around Mega Garchomp and Dragapult as dual Dragon threats. Weavile handles Psychic and Ghost types while Scizor provides priority.',
    strategy: 'Lead with Dragapult to scout and deal early damage. Bring in Mega Garchomp mid-game to clean up weakened teams. Weavile and Scizor provide priority to pick off threats. Clefable offers Fairy coverage and Calm Mind sweeping potential. Rotom-Wash handles bulky Water and Ground types.',
    leadRecommendations: [
      { pokemon: ['Dragapult'], situation: 'Default lead — fast scout with great coverage' },
      { pokemon: ['Weavile'], situation: 'Against Psychic/Ghost heavy teams — priority + Knock Off' },
      { pokemon: ['Rotom-Wash'], situation: 'Against physical attackers — bulk + Volt Switch pivoting' },
    ],
    backlineNotes: 'Mega Garchomp is the mid-game breaker. Save Scizor for late-game Bullet Punch cleanup.',
    pokemon: [
      { name: 'Garchomp', types: ['Dragon', 'Ground'], ability: 'Rough Skin', nature: 'Jolly', statPoints: { hp: 0, atk: 32, def: 0, spa: 0, spd: 2, spe: 32 }, item: 'Garchompite', moves: ['Earthquake', 'Dragon Claw', 'Swords Dance', 'Rock Slide'], role: 'Mega Sweeper', baseStats: { hp: 108, atk: 130, def: 95, spa: 80, spd: 85, spe: 102 } },
      { name: 'Dragapult', types: ['Dragon', 'Ghost'], ability: 'Clear Body', nature: 'Timid', statPoints: { hp: 0, atk: 0, def: 0, spa: 32, spd: 2, spe: 32 }, item: 'Focus Sash', moves: ['Shadow Ball', 'Draco Meteor', 'Thunderbolt', 'U-turn'], role: 'Lead / Scout', baseStats: { hp: 88, atk: 120, def: 75, spa: 100, spd: 75, spe: 142 } },
      { name: 'Weavile', types: ['Dark', 'Ice'], ability: 'Pressure', nature: 'Jolly', statPoints: { hp: 0, atk: 32, def: 2, spa: 0, spd: 0, spe: 32 }, item: 'Focus Sash', moves: ['Triple Axel', 'Knock Off', 'Ice Shard', 'Swords Dance'], role: 'Revenge Killer', baseStats: { hp: 70, atk: 120, def: 65, spa: 45, spd: 85, spe: 125 } },
      { name: 'Scizor', types: ['Bug', 'Steel'], ability: 'Technician', nature: 'Adamant', statPoints: { hp: 32, atk: 32, def: 0, spa: 0, spd: 2, spe: 0 }, item: 'Leftovers', moves: ['Bullet Punch', 'U-turn', 'Swords Dance', 'Roost'], role: 'Priority / Pivot', baseStats: { hp: 70, atk: 130, def: 100, spa: 55, spd: 80, spe: 65 } },
      { name: 'Clefable', types: ['Fairy'], ability: 'Magic Guard', nature: 'Bold', statPoints: { hp: 32, atk: 0, def: 32, spa: 0, spd: 2, spe: 0 }, item: 'Leftovers', moves: ['Moonblast', 'Calm Mind', 'Flamethrower', 'Protect'], role: 'Special Tank', baseStats: { hp: 95, atk: 70, def: 73, spa: 95, spd: 90, spe: 60 } },
      { name: 'Rotom-Wash', types: ['Electric', 'Water'], ability: 'Levitate', nature: 'Bold', statPoints: { hp: 32, atk: 0, def: 27, spa: 0, spd: 7, spe: 0 }, item: 'Sitrus Berry', moves: ['Hydro Pump', 'Volt Switch', 'Thunder Wave', 'Protect'], role: 'Defensive Pivot', baseStats: { hp: 50, atk: 65, def: 107, spa: 105, spd: 107, spe: 86 } },
    ],
  },
  {
    id: 'singles-stall-balance', format: 'singles',
    name: 'Fortress Wall',
    archetype: 'Stall / Balance',
    difficulty: 'Advanced',
    description: 'A defensive singles team that grinds opponents down with status, hazards, and recovery. Mega Sableye bounces back hazards while Toxapex and Corviknight form an almost unbreakable defensive core.',
    strategy: 'Set up Stealth Rock with Hippowdon, then pivot between Toxapex, Corviknight, and Mega Sableye to wall threats. Haze from Toxapex prevents setup sweepers. Clefable provides offensive pressure with Calm Mind when an opening appears.',
    leadRecommendations: [
      { pokemon: ['Hippowdon'], situation: 'Default — set Stealth Rock and tank hits' },
      { pokemon: ['Sableye'], situation: 'Against hazard leads — Magic Bounce reflects them' },
      { pokemon: ['Corviknight'], situation: 'Against physical attackers — wall and Roost' },
    ],
    backlineNotes: 'Mega Sableye is the anti-hazard anchor. Clefable is the win condition — set up Calm Mind once threats are weakened.',
    pokemon: [
      { name: 'Sableye', types: ['Dark', 'Ghost'], ability: 'Prankster', nature: 'Careful', statPoints: { hp: 32, atk: 0, def: 17, spa: 0, spd: 17, spe: 0 }, item: 'Sablenite', moves: ['Knock Off', 'Will-O-Wisp', 'Recover', 'Taunt'], role: 'Mega / Hazard Control', baseStats: { hp: 50, atk: 75, def: 75, spa: 65, spd: 65, spe: 50 } },
      { name: 'Toxapex', types: ['Poison', 'Water'], ability: 'Regenerator', nature: 'Bold', statPoints: { hp: 32, atk: 0, def: 32, spa: 0, spd: 2, spe: 0 }, item: 'Sitrus Berry', moves: ['Scald', 'Toxic', 'Haze', 'Recover'], role: 'Physical Wall', baseStats: { hp: 50, atk: 63, def: 152, spa: 53, spd: 142, spe: 35 } },
      { name: 'Corviknight', types: ['Flying', 'Steel'], ability: 'Mirror Armor', nature: 'Impish', statPoints: { hp: 32, atk: 0, def: 23, spa: 0, spd: 11, spe: 0 }, item: 'Leftovers', moves: ['Brave Bird', 'Roost', 'U-turn', 'Taunt'], role: 'Physical Wall / Pivot', baseStats: { hp: 98, atk: 87, def: 105, spa: 53, spd: 85, spe: 67 } },
      { name: 'Hippowdon', types: ['Ground'], ability: 'Sand Stream', nature: 'Impish', statPoints: { hp: 32, atk: 0, def: 27, spa: 0, spd: 7, spe: 0 }, item: 'Leftovers', moves: ['Earthquake', 'Stealth Rock', 'Slack Off', 'Whirlwind'], role: 'Hazard Setter / Tank', baseStats: { hp: 108, atk: 112, def: 118, spa: 68, spd: 72, spe: 47 } },
      { name: 'Clefable', types: ['Fairy'], ability: 'Magic Guard', nature: 'Bold', statPoints: { hp: 32, atk: 0, def: 22, spa: 12, spd: 0, spe: 0 }, item: 'Leftovers', moves: ['Moonblast', 'Calm Mind', 'Flamethrower', 'Protect'], role: 'Win Condition', baseStats: { hp: 95, atk: 70, def: 73, spa: 95, spd: 90, spe: 60 } },
      { name: 'Chandelure', types: ['Ghost', 'Fire'], ability: 'Flash Fire', nature: 'Timid', statPoints: { hp: 0, atk: 0, def: 0, spa: 32, spd: 2, spe: 32 }, item: 'Focus Sash', moves: ['Shadow Ball', 'Flamethrower', 'Energy Ball', 'Trick'], role: 'Special Breaker', baseStats: { hp: 60, atk: 55, def: 90, spa: 145, spd: 90, spe: 80 } },
    ],
  },
  {
    id: 'singles-voltturn', format: 'singles',
    name: 'Volt-Turn Cycle',
    archetype: 'Pivot Offense',
    difficulty: 'Beginner',
    description: 'A momentum-based team that uses U-turn and Volt Switch to keep constant pressure. Mega Scizor is the centerpiece with Technician-boosted Bullet Punch and U-turn.',
    strategy: 'Pivot constantly with U-turn and Volt Switch to keep the right matchup on the field. Mega Scizor breaks walls with Swords Dance + Bullet Punch. Rotom-Heat handles Steel types. Greninja provides speed and unpredictable coverage.',
    leadRecommendations: [
      { pokemon: ['Greninja'], situation: 'Default — fast Protean coverage to scout' },
      { pokemon: ['Rotom-Heat'], situation: 'Against Steel-heavy teams — Overheat + Volt Switch' },
      { pokemon: ['Dragonite'], situation: 'Against offensive teams — Multiscale tanks a hit' },
    ],
    backlineNotes: 'Mega Scizor comes in mid-game after walls are chipped. Dragonite with Multiscale cleans endgame.',
    pokemon: [
      { name: 'Scizor', types: ['Bug', 'Steel'], ability: 'Technician', nature: 'Adamant', statPoints: { hp: 32, atk: 32, def: 0, spa: 0, spd: 2, spe: 0 }, item: 'Scizorite', moves: ['Bullet Punch', 'U-turn', 'Swords Dance', 'Roost'], role: 'Mega Breaker / Pivot', baseStats: { hp: 70, atk: 130, def: 100, spa: 55, spd: 80, spe: 65 } },
      { name: 'Greninja', types: ['Water', 'Dark'], ability: 'Protean', nature: 'Timid', statPoints: { hp: 0, atk: 0, def: 0, spa: 32, spd: 2, spe: 32 }, item: 'Focus Sash', moves: ['Hydro Pump', 'Dark Pulse', 'Ice Beam', 'U-turn'], role: 'Lead / Revenge Killer', baseStats: { hp: 72, atk: 95, def: 67, spa: 103, spd: 71, spe: 122 } },
      { name: 'Rotom-Heat', types: ['Electric', 'Fire'], ability: 'Levitate', nature: 'Modest', statPoints: { hp: 32, atk: 0, def: 0, spa: 32, spd: 2, spe: 0 }, item: 'Sitrus Berry', moves: ['Overheat', 'Volt Switch', 'Will-O-Wisp', 'Thunderbolt'], role: 'Special Pivot', baseStats: { hp: 50, atk: 65, def: 107, spa: 105, spd: 107, spe: 86 } },
      { name: 'Dragonite', types: ['Dragon', 'Flying'], ability: 'Multiscale', nature: 'Adamant', statPoints: { hp: 0, atk: 32, def: 0, spa: 0, spd: 2, spe: 32 }, item: 'Leftovers', moves: ['Dragon Dance', 'Extreme Speed', 'Earthquake', 'Roost'], role: 'Setup Sweeper', baseStats: { hp: 91, atk: 134, def: 95, spa: 100, spd: 100, spe: 80 } },
      { name: 'Tyranitar', types: ['Rock', 'Dark'], ability: 'Sand Stream', nature: 'Adamant', statPoints: { hp: 32, atk: 32, def: 0, spa: 0, spd: 2, spe: 0 }, item: 'Leftovers', moves: ['Stone Edge', 'Crunch', 'Stealth Rock', 'Earthquake'], role: 'Hazards / Tank', baseStats: { hp: 100, atk: 134, def: 110, spa: 95, spd: 100, spe: 61 } },
      { name: 'Sylveon', types: ['Fairy'], ability: 'Pixilate', nature: 'Modest', statPoints: { hp: 32, atk: 0, def: 0, spa: 32, spd: 2, spe: 0 }, item: 'Sitrus Berry', moves: ['Hyper Voice', 'Calm Mind', 'Protect', 'Moonblast'], role: 'Special Wall / Wincon', baseStats: { hp: 95, atk: 65, def: 65, spa: 110, spd: 130, spe: 60 } },
    ],
  },
  {
    id: 'singles-floettes-fury', format: 'singles',
    name: "Floette's Fury",
    archetype: 'Anti-meta Offense',
    difficulty: 'Advanced',
    description: 'A creative Master Ball-tier team built around Mega Floette as a wallbreaker lead, with Mamoswine as the Sash anchor and Volcarona as the surprise sweeper. Niche sets (Chilling Water Floette, Rock Tomb Mamoswine, Dragon Dance Dragonite) prey on common meta picks.',
    strategy: `Lead Floette ~95% of games to break the opponent open with Light of Ruin while Chilling Water + Protect win key 1v1s (Kangaskhan, Mimikyu, Glimmora). Mamoswine is the second-most-brought (~80%) — Ice Shard cleanup and Sash insurance vs Garchomp/Gengar/Aegislash. Volcarona is the anti-meta sweeper that sets up on Mega Kangaskhan Double-Edge and Primarina. Basculegion cleans late game and OHKOs Mega Gengar/Lucario/Delphox. Dragonite is the stallbreaker — DD twice against Toxapex behind Lum Berry, then Fire Punch the predicted Corviknight switch. Umbreon is a team-preview scarecrow only — never actually brought.`,
    leadRecommendations: [
      { pokemon: ['Floette'], situation: 'Default — wallbreaker with Chilling Water + Protect scouting' },
      { pokemon: ['Mamoswine'], situation: 'Vs Gengar / Starmie / Basculegion / Greninja leads — Floette pivots out' },
      { pokemon: ['Volcarona'], situation: 'Vs Mega Kangaskhan or Primarina lead — free QD setup' },
    ],
    backlineNotes: 'Basculegion is the late-game cleaner once Floette has broken things open (Last Respects sweeps unprepared teams). Dragonite only when stall (Toxapex/Slowking + Corviknight/Umbreon) or Mega Venusaur is detected. Umbreon stays in the back as a Gengar/Delphox/Greninja scarecrow.',
    advisorStrategy: {
      bringPriors: {
        Floette: 0.95,
        Mamoswine: 0.80,
        Volcarona: 0.55,
        Basculegion: 0.55,
        Dragonite: 0.35,
        Umbreon: 0.0,
      },
      triggers: [
        { id: 'ff-stall', when: { sees: ['Toxapex', 'Slowking-Galar', 'Slowking'], andAlso: [], megaOnly: false, archetype: 'Stall' }, bring: ['Dragonite', 'Basculegion', 'Floette'], reason: 'Stall core (Tox/Slowking + Corv/Umbreon) — Dragonite DD breaks Toxapex behind Lum, Bascul cleans, Floette wallbreaker' },
        { id: 'ff-mvenu', when: { sees: ['Venusaur'], andAlso: [], megaOnly: true, archetype: '' }, bring: ['Dragonite'], reason: 'Mega Venusaur — Dragonite handles it' },
        { id: 'ff-mkanga', when: { sees: ['Kangaskhan'], andAlso: [], megaOnly: true, archetype: '' }, bring: ['Volcarona'], reason: 'Mega Kangaskhan goes Double-Edge to dodge Fake Out burn — free Quiver Dance' },
        { id: 'ff-prim', when: { sees: ['Primarina'], andAlso: [], megaOnly: false, archetype: '' }, bring: ['Volcarona'], reason: 'Primarina — set up 2 QDs and Giga Drain x2 to KO' },
        { id: 'ff-mdelphox', when: { sees: ['Delphox'], andAlso: [], megaOnly: true, archetype: '' }, bring: ['Basculegion'], reason: 'Mega Delphox — Flip Turn OHKO' },
        { id: 'ff-mgengar', when: { sees: ['Gengar'], andAlso: [], megaOnly: true, archetype: '' }, bring: ['Basculegion'], reason: 'Mega Gengar — Wave Crash OHKO' },
        { id: 'ff-mluc', when: { sees: ['Lucario'], andAlso: [], megaOnly: true, archetype: '' }, bring: ['Basculegion'], reason: 'Mega Lucario — Wave Crash OHKO' },
        { id: 'ff-garaegi', when: { sees: ['Garchomp', 'Aegislash'], andAlso: [], megaOnly: false, archetype: '' }, bring: ['Mamoswine'], reason: 'Garchomp / Aegislash — Mamoswine preys on both (Ice Shard 2HKO Chomp, EQ Aegislash)' },
        { id: 'ff-mcharygyra', when: { sees: ['Charizard', 'Gyarados'], andAlso: [], megaOnly: false, archetype: '' }, bring: ['Mamoswine'], reason: 'Mega Char-Y / Gyarados — Rock Tomb handles Char-Y, stops Gyara setup' },
        { id: 'ff-batonpass', when: { sees: [], andAlso: [], megaOnly: false, archetype: 'Baton Pass' }, bring: ['Floette'], reason: 'Floette breaks Baton Pass teams even after Calm Mind passes' },
        { id: 'ff-ghost-checks', when: { sees: ['Gengar', 'Delphox'], andAlso: [], megaOnly: false, archetype: '' }, bring: ['Dragonite'], reason: 'Non-mega Gengar/Delphox — Multiscale Dragonite checks them' },
      ],
      leadRules: {
        defaultLead: 'Floette',
        avoid: [
          { lead: 'Floette', into: ['Gengar', 'Starmie', 'Basculegion', 'Greninja'], reason: "Floette can't pressure these leads — pivot to Mamoswine or Volcarona" },
        ],
      },
      setupScripts: [
        { mon: 'Volcarona', vs: ['Kangaskhan'], script: 'Mega Kanga goes Double-Edge to dodge Fake Out burn → QD → Sitrus procs → Morning Sun → Fiery Dance for 75%+ burn → KO (watch for Garchomp follow-up)' },
        { mon: 'Volcarona', vs: ['Primarina'], script: 'Set up 1st QD as opp pivots to Primarina → tank a hit → 2nd QD → Giga Drain x2 to KO' },
        { mon: 'Volcarona', vs: ['Aegislash'], script: 'Aegi goes Poltergeist → Sitrus procs → free QD → forces switch (Aegi locked, has to go)' },
        { mon: 'Dragonite', vs: ['Toxapex'], script: 'Switch in as opp pivots to Toxapex → Lum eats Toxic 1 → DD → DD on Toxic 2 → predict Corviknight switch → Fire Punch' },
        { mon: 'Floette', vs: ['Kangaskhan'], script: 'Lead Protect to scout Fake Out → Chilling Water to win the 1v1 (drops Atk + chip)' },
      ],
      keyOneVOnes: [
        { mon: 'Floette', vs: 'Kangaskhan', note: 'WIN with Chilling Water after Protect (drops Atk, chip wins it)' },
        { mon: 'Floette', vs: 'Mimikyu', note: 'WIN with Chilling Water (busts Disguise + Atk drop)' },
        { mon: 'Floette', vs: 'Glimmora', note: '2HKO Chilling Water, no recoil' },
        { mon: 'Floette', vs: 'Archaludon', note: 'OHKO with Light of Ruin' },
        { mon: 'Floette', vs: 'Hippowdon', note: 'Wins lead — break it open' },
        { mon: 'Floette', vs: 'Garchomp', note: 'Speed tie — Light of Ruin if you win' },
        { mon: 'Floette', vs: 'Meowscarada', note: 'Light of Ruin OHKO range; Protect scouts Scarf U-turn' },
        { mon: 'Mamoswine', vs: 'Hydreigon', note: 'Ice Shard 2HKOs' },
        { mon: 'Mamoswine', vs: 'Meowscarada', note: 'Ice Shard 2HKOs' },
        { mon: 'Mamoswine', vs: 'Dragonite', note: 'Ice Shard 2HKOs (watch Multiscale)' },
        { mon: 'Mamoswine', vs: 'Garchomp', note: 'Ice Shard 2HKOs — preys on Garchomp' },
        { mon: 'Mamoswine', vs: 'Kingambit', note: 'Outspeeds speed-invested Kingambit; EQ KOs' },
        { mon: 'Mamoswine', vs: 'Charizard', note: 'Rock Tomb handles Mega Char-Y' },
        { mon: 'Mamoswine', vs: 'Gyarados', note: 'Rock Tomb stops DD setup' },
        { mon: 'Volcarona', vs: 'Kangaskhan', note: 'After +1 QD: tanks Double-Edge → Fiery Dance burn (>75%) wins' },
        { mon: 'Volcarona', vs: 'Garchomp', note: 'LOSS — do not commit sweep with Chomp on the field' },
        { mon: 'Basculegion', vs: 'Gengar', note: 'Wave Crash OHKOs Mega Gengar' },
        { mon: 'Basculegion', vs: 'Lucario', note: 'Wave Crash OHKOs Mega Lucario' },
        { mon: 'Basculegion', vs: 'Delphox', note: 'Flip Turn OHKOs Mega Delphox' },
        { mon: 'Basculegion', vs: 'Floette', note: 'Wave Crash has a chance to OHKO Mega Floette' },
        { mon: 'Basculegion', vs: 'Basculegion', note: 'Jolly wins ~67% of mirror speed ties (33% are Adamant)' },
        { mon: 'Dragonite', vs: 'Toxapex', note: 'EQ + DD setup; Lum berry eats Toxic once' },
        { mon: 'Dragonite', vs: 'Slowking-Galar', note: 'EQ breaks it' },
        { mon: 'Dragonite', vs: 'Corviknight', note: 'Fire Punch is weak — only commit on a predicted switch' },
        { mon: 'Dragonite', vs: 'Gengar', note: 'Multiscale check (only at full HP)' },
      ],
    },
    pokemon: [
      {
        name: 'Floette',
        types: ['Fairy'],
        ability: 'Fairy Aura',
        nature: 'Modest',
        statPoints: { hp: 2, atk: 0, def: 0, spa: 32, spd: 0, spe: 32 },
        item: 'Floettite',
        moves: ['Light of Ruin', 'Chilling Water', 'Protect', 'Moonblast'],
        role: 'Mega / Wallbreaker Lead',
        notes: "Center of the team — brought ~95% of games. Light of Ruin breaks open most leads (OHKOs Archaludon). Chilling Water is the niche pick: wins the 1v1 vs Kanga after Protect, beats Mimikyu, 2HKOs Glimmora without recoil. Protect scouts Fake Out (Kanga/Lopunny), Protean Greninja, and Scarf U-turn (Meow/Hydreigon/Bascul/Rotom). Just breaks the enemy team open — has even won games against Baton Pass after Calm Mind.",
        baseStats: { hp: 54, atk: 45, def: 47, spa: 75, spd: 98, spe: 52 },
        megaStats: { hp: 54, atk: 45, def: 67, spa: 125, spd: 128, spe: 72 },
      },
      {
        name: 'Mamoswine',
        types: ['Ice', 'Ground'],
        ability: 'Thick Fat',
        nature: 'Adamant',
        statPoints: { hp: 16, atk: 16, def: 0, spa: 0, spd: 0, spe: 32 },
        item: 'Focus Sash',
        moves: ['Earthquake', 'Icicle Crash', 'Ice Shard', 'Rock Tomb'],
        role: 'Sash Anchor / Revenge Killer',
        notes: 'Brought ~80% — second-most-used. Anti-meta vs Garchomp/Gengar/Aegislash. Ice Shard 2HKOs Hydreigon, Meowscarada, Dragonite, and Garchomp. Rock Tomb is the niche tech — handles Mega Char-Y, stops Gyarados DD, and breaks Bascul speed tie when Sash is intact. Icicle Crash over Spear for the flinch hax on Hippowdon-tier tanks. Speed EVs outpace speed-invested Kingambit; HP investment lets it tank a hit and revenge-kill.',
        baseStats: { hp: 110, atk: 130, def: 80, spa: 70, spd: 60, spe: 80 },
      },
      {
        name: 'Volcarona',
        types: ['Bug', 'Fire'],
        ability: 'Swarm',
        nature: 'Timid',
        statPoints: { hp: 16, atk: 0, def: 16, spa: 0, spd: 0, spe: 16 },
        item: 'Sitrus Berry',
        moves: ['Quiver Dance', 'Fiery Dance', 'Giga Drain', 'Morning Sun'],
        role: 'Setup Sweeper',
        notes: 'The savior. Anti-meta bulky QD set — Timid for nature bonus, Speed EVs to outspeed Scarf Bascul after +1 (when chipped), rest in HP/Def. HP is even so Sitrus procs after Stealth Rocks. Free switch-ins on Meowscarada, Scizor, Aegislash, Corviknight, Kingambit. Main prey: Mega Kangaskhan (Double-Edge → QD → Sitrus → Morning Sun → Fiery Dance burn) and Primarina (2 QDs, Giga Drain x2). Loses to Garchomp — remove it before sweeping.',
        baseStats: { hp: 85, atk: 60, def: 65, spa: 135, spd: 105, spe: 100 },
      },
      {
        name: 'Basculegion',
        types: ['Water', 'Ghost'],
        ability: 'Adaptability',
        nature: 'Jolly',
        statPoints: { hp: 0, atk: 32, def: 0, spa: 0, spd: 2, spe: 32 },
        item: 'Choice Scarf',
        moves: ['Wave Crash', 'Last Respects', 'Flip Turn', 'Aqua Jet'],
        role: 'Late-game Cleaner / Mega-killer',
        notes: 'Near-perfect synergy with Floette — absorbs Steel/Fire/Poison while Floette eats Dark for it. Jolly wins ~67% of mirror speed ties (33% of Basculegions are Adamant). Wave Crash OHKOs Mega Gengar, Mega Lucario, has a chance on Mega Floette. Flip Turn OHKOs Mega Delphox. Last Respects sweeps unprepared teams late game.',
        baseStats: { hp: 120, atk: 112, def: 65, spa: 80, spd: 75, spe: 78 },
      },
      {
        name: 'Dragonite',
        types: ['Dragon', 'Flying'],
        ability: 'Multiscale',
        nature: 'Adamant',
        statPoints: { hp: 0, atk: 32, def: 0, spa: 0, spd: 2, spe: 32 },
        item: 'Lum Berry',
        moves: ['Dragon Dance', 'Earthquake', 'Fire Punch', 'Extreme Speed'],
        role: 'Stallbreaker / DD Setup',
        notes: "Niche non-mega DD set — the team's stallbreaker. Brought vs stall (Toxapex/Slowking + Corv/Umbreon) and vs Mega Venusaur. The play: switch in as opp goes Toxapex (typically off Floette lead or Bascul Flip Turn) — Lum eats Toxic 1 → DD → DD on Toxic 2 → predict Corv switch → Fire Punch wins. EQ breaks Galarian Slowking and Toxapex. Fire Punch is weaker than you'd want — only commit on a predicted switch. Also useful as a Multiscale check vs Gengar/Delphox/Bascul.",
        baseStats: { hp: 91, atk: 134, def: 95, spa: 100, spd: 100, spe: 80 },
      },
      {
        name: 'Umbreon',
        types: ['Dark'],
        ability: 'Synchronize',
        nature: 'Calm',
        statPoints: { hp: 32, atk: 0, def: 2, spa: 0, spd: 32, spe: 0 },
        item: 'Leftovers',
        moves: ['Foul Play', 'Yawn', 'Wish', 'Protect'],
        role: 'Team-preview Scarecrow',
        notes: 'Bring rate: ~0%. Pure team-preview psyops — its presence discourages opponents from bringing Gengar / Delphox / Greninja / Starmie. Standard defensive set. Has never actually been brought into a game — slot was Emboar → Gyarados → Greninja → Umbreon, and the win rate was best with Umbreon scaring threats off in preview.',
        baseStats: { hp: 95, atk: 65, def: 110, spa: 60, spd: 130, spe: 65 },
      },
    ],
  },
];

// ============================================================
// POKEMON CHAMPIONS FULL ROSTER (186 Pokemon + Megas)
// Only Pokemon available in Pokemon Champions
// ============================================================
export const ALL_POKEMON = [
  // Kanto
  { name: 'Venusaur', types: ['Grass', 'Poison'] },
  { name: 'Charizard', types: ['Fire', 'Flying'] },
  { name: 'Blastoise', types: ['Water'] },
  { name: 'Beedrill', types: ['Bug', 'Poison'] },
  { name: 'Pidgeot', types: ['Normal', 'Flying'] },
  { name: 'Arbok', types: ['Poison'] },
  { name: 'Pikachu', types: ['Electric'] },
  { name: 'Raichu', types: ['Electric'] },
  { name: 'Raichu-Alola', types: ['Electric', 'Psychic'] },
  { name: 'Clefable', types: ['Fairy'] },
  { name: 'Ninetales', types: ['Fire'] },
  { name: 'Ninetales-Alola', types: ['Ice', 'Fairy'] },
  { name: 'Arcanine', types: ['Fire'] },
  { name: 'Arcanine-Hisui', types: ['Fire', 'Rock'] },
  { name: 'Alakazam', types: ['Psychic'] },
  { name: 'Machamp', types: ['Fighting'] },
  { name: 'Victreebel', types: ['Grass', 'Poison'] },
  { name: 'Slowbro', types: ['Water', 'Psychic'] },
  { name: 'Slowbro-Galarian', types: ['Poison', 'Psychic'] },
  { name: 'Gengar', types: ['Ghost', 'Poison'] },
  { name: 'Kangaskhan', types: ['Normal'] },
  { name: 'Starmie', types: ['Water', 'Psychic'] },
  { name: 'Pinsir', types: ['Bug'] },
  { name: 'Tauros', types: ['Normal'] },
  { name: 'Tauros-Paldea', types: ['Fighting'] },
  { name: 'Gyarados', types: ['Water', 'Flying'] },
  { name: 'Ditto', types: ['Normal'] },
  { name: 'Vaporeon', types: ['Water'] },
  { name: 'Jolteon', types: ['Electric'] },
  { name: 'Flareon', types: ['Fire'] },
  { name: 'Aerodactyl', types: ['Rock', 'Flying'] },
  { name: 'Snorlax', types: ['Normal'] },
  { name: 'Dragonite', types: ['Dragon', 'Flying'] },
  // Johto
  { name: 'Meganium', types: ['Grass'] },
  { name: 'Typhlosion', types: ['Fire'] },
  { name: 'Typhlosion-Hisui', types: ['Fire', 'Ghost'] },
  { name: 'Feraligatr', types: ['Water'] },
  { name: 'Ariados', types: ['Bug', 'Poison'] },
  { name: 'Ampharos', types: ['Electric'] },
  { name: 'Azumarill', types: ['Water', 'Fairy'] },
  { name: 'Politoed', types: ['Water'] },
  { name: 'Espeon', types: ['Psychic'] },
  { name: 'Umbreon', types: ['Dark'] },
  { name: 'Slowking', types: ['Water', 'Psychic'] },
  { name: 'Slowking-Galarian', types: ['Poison', 'Psychic'] },
  { name: 'Forretress', types: ['Bug', 'Steel'] },
  { name: 'Steelix', types: ['Steel', 'Ground'] },
  { name: 'Scizor', types: ['Bug', 'Steel'] },
  { name: 'Heracross', types: ['Bug', 'Fighting'] },
  { name: 'Skarmory', types: ['Steel', 'Flying'] },
  { name: 'Houndoom', types: ['Dark', 'Fire'] },
  { name: 'Tyranitar', types: ['Rock', 'Dark'] },
  // Hoenn
  { name: 'Pelipper', types: ['Water', 'Flying'] },
  { name: 'Gardevoir', types: ['Psychic', 'Fairy'] },
  { name: 'Sableye', types: ['Dark', 'Ghost'] },
  { name: 'Aggron', types: ['Steel', 'Rock'] },
  { name: 'Medicham', types: ['Fighting', 'Psychic'] },
  { name: 'Electrike', types: ['Electric'] },
  { name: 'Manectric', types: ['Electric'] },
  { name: 'Sharpedo', types: ['Water', 'Dark'] },
  { name: 'Camerupt', types: ['Fire', 'Ground'] },
  { name: 'Torkoal', types: ['Fire'] },
  { name: 'Altaria', types: ['Dragon', 'Flying'] },
  { name: 'Milotic', types: ['Water'] },
  { name: 'Castform', types: ['Normal'] },
  { name: 'Banette', types: ['Ghost'] },
  { name: 'Chimecho', types: ['Psychic'] },
  { name: 'Absol', types: ['Dark'] },
  { name: 'Glalie', types: ['Ice'] },
  // Sinnoh
  { name: 'Torterra', types: ['Grass', 'Ground'] },
  { name: 'Infernape', types: ['Fire', 'Fighting'] },
  { name: 'Empoleon', types: ['Water', 'Steel'] },
  { name: 'Luxray', types: ['Electric'] },
  { name: 'Roserade', types: ['Grass', 'Poison'] },
  { name: 'Rampardos', types: ['Rock'] },
  { name: 'Bastiodon', types: ['Rock', 'Steel'] },
  { name: 'Lopunny', types: ['Normal'] },
  { name: 'Spiritomb', types: ['Ghost', 'Dark'] },
  { name: 'Garchomp', types: ['Dragon', 'Ground'] },
  { name: 'Lucario', types: ['Fighting', 'Steel'] },
  { name: 'Hippowdon', types: ['Ground'] },
  { name: 'Toxicroak', types: ['Poison', 'Fighting'] },
  { name: 'Abomasnow', types: ['Grass', 'Ice'] },
  { name: 'Weavile', types: ['Dark', 'Ice'] },
  { name: 'Rhyperior', types: ['Ground', 'Rock'] },
  { name: 'Leafeon', types: ['Grass'] },
  { name: 'Glaceon', types: ['Ice'] },
  { name: 'Gliscor', types: ['Ground', 'Flying'] },
  { name: 'Mamoswine', types: ['Ice', 'Ground'] },
  { name: 'Gallade', types: ['Psychic', 'Fighting'] },
  { name: 'Froslass', types: ['Ice', 'Ghost'] },
  { name: 'Rotom', types: ['Electric', 'Ghost'] },
  { name: 'Rotom-Heat', types: ['Electric', 'Fire'] },
  { name: 'Rotom-Wash', types: ['Electric', 'Water'] },
  { name: 'Rotom-Frost', types: ['Electric', 'Ice'] },
  { name: 'Rotom-Fan', types: ['Electric', 'Flying'] },
  { name: 'Rotom-Mow', types: ['Electric', 'Grass'] },
  // Unova
  { name: 'Serperior', types: ['Grass'] },
  { name: 'Emboar', types: ['Fire', 'Fighting'] },
  { name: 'Samurott', types: ['Water'] },
  { name: 'Samurott-Hisui', types: ['Water', 'Dark'] },
  { name: 'Watchog', types: ['Normal'] },
  { name: 'Liepard', types: ['Dark'] },
  { name: 'Simisage', types: ['Grass'] },
  { name: 'Simisear', types: ['Fire'] },
  { name: 'Simipour', types: ['Water'] },
  { name: 'Excadrill', types: ['Ground', 'Steel'] },
  { name: 'Audino', types: ['Normal'] },
  { name: 'Conkeldurr', types: ['Fighting'] },
  { name: 'Whimsicott', types: ['Grass', 'Fairy'] },
  { name: 'Krookodile', types: ['Ground', 'Dark'] },
  { name: 'Cofagrigus', types: ['Ghost'] },
  { name: 'Garbodor', types: ['Poison'] },
  { name: 'Zoroark', types: ['Dark'] },
  { name: 'Zoroark-Hisui', types: ['Normal', 'Ghost'] },
  { name: 'Reuniclus', types: ['Psychic'] },
  { name: 'Vanilluxe', types: ['Ice'] },
  { name: 'Emolga', types: ['Electric', 'Flying'] },
  { name: 'Chandelure', types: ['Ghost', 'Fire'] },
  { name: 'Beartic', types: ['Ice'] },
  { name: 'Stunfisk', types: ['Ground', 'Electric'] },
  { name: 'Stunfisk-Galarian', types: ['Ground', 'Steel'] },
  { name: 'Golurk', types: ['Ground', 'Ghost'] },
  { name: 'Hydreigon', types: ['Dark', 'Dragon'] },
  { name: 'Volcarona', types: ['Bug', 'Fire'] },
  // Kalos
  { name: 'Chesnaught', types: ['Grass', 'Fighting'] },
  { name: 'Delphox', types: ['Fire', 'Psychic'] },
  { name: 'Greninja', types: ['Water', 'Dark'] },
  { name: 'Diggersby', types: ['Normal', 'Ground'] },
  { name: 'Talonflame', types: ['Fire', 'Flying'] },
  { name: 'Vivillon', types: ['Bug', 'Flying'] },
  { name: 'Floette', types: ['Fairy'] },
  { name: 'Florges', types: ['Fairy'] },
  { name: 'Pangoro', types: ['Fighting', 'Dark'] },
  { name: 'Furfrou', types: ['Normal'] },
  { name: 'Meowstic', types: ['Psychic'] },
  { name: 'Aegislash', types: ['Steel', 'Ghost'] },
  { name: 'Aromatisse', types: ['Fairy'] },
  { name: 'Slurpuff', types: ['Fairy'] },
  { name: 'Clawitzer', types: ['Water'] },
  { name: 'Heliolisk', types: ['Electric', 'Normal'] },
  { name: 'Tyrantrum', types: ['Rock', 'Dragon'] },
  { name: 'Aurorus', types: ['Rock', 'Ice'] },
  { name: 'Sylveon', types: ['Fairy'] },
  { name: 'Hawlucha', types: ['Fighting', 'Flying'] },
  { name: 'Dedenne', types: ['Electric', 'Fairy'] },
  { name: 'Goodra', types: ['Dragon'] },
  { name: 'Goodra-Hisui', types: ['Steel', 'Dragon'] },
  { name: 'Klefki', types: ['Steel', 'Fairy'] },
  { name: 'Trevenant', types: ['Ghost', 'Grass'] },
  { name: 'Gourgeist', types: ['Ghost', 'Grass'] },
  { name: 'Avalugg', types: ['Ice'] },
  { name: 'Avalugg-Hisui', types: ['Ice', 'Rock'] },
  { name: 'Noivern', types: ['Flying', 'Dragon'] },
  // Alola
  { name: 'Decidueye', types: ['Grass', 'Ghost'] },
  { name: 'Decidueye-Hisui', types: ['Grass', 'Fighting'] },
  { name: 'Incineroar', types: ['Fire', 'Dark'] },
  { name: 'Primarina', types: ['Water', 'Fairy'] },
  { name: 'Toucannon', types: ['Normal', 'Flying'] },
  { name: 'Crabominable', types: ['Fighting', 'Ice'] },
  { name: 'Lycanroc', types: ['Rock'] },
  { name: 'Toxapex', types: ['Poison', 'Water'] },
  { name: 'Mudsdale', types: ['Ground'] },
  { name: 'Araquanid', types: ['Water', 'Bug'] },
  { name: 'Salazzle', types: ['Poison', 'Fire'] },
  { name: 'Tsareena', types: ['Grass'] },
  { name: 'Oranguru', types: ['Normal', 'Psychic'] },
  { name: 'Passimian', types: ['Fighting'] },
  { name: 'Mimikyu', types: ['Ghost', 'Fairy'] },
  { name: 'Drampa', types: ['Normal', 'Dragon'] },
  { name: 'Kommo-o', types: ['Dragon', 'Fighting'] },
  // Galar
  { name: 'Corviknight', types: ['Flying', 'Steel'] },
  { name: 'Flapple', types: ['Grass', 'Dragon'] },
  { name: 'Appletun', types: ['Grass', 'Dragon'] },
  { name: 'Sandaconda', types: ['Ground'] },
  { name: 'Polteageist', types: ['Ghost'] },
  { name: 'Hatterene', types: ['Psychic', 'Fairy'] },
  { name: 'Mr. Rime', types: ['Ice', 'Psychic'] },
  { name: 'Runerigus', types: ['Ground', 'Ghost'] },
  { name: 'Alcremie', types: ['Fairy'] },
  { name: 'Morpeko', types: ['Electric', 'Dark'] },
  { name: 'Dragapult', types: ['Dragon', 'Ghost'] },
  // Hisui
  { name: 'Wyrdeer', types: ['Normal', 'Psychic'] },
  { name: 'Kleavor', types: ['Bug', 'Rock'] },
  { name: 'Basculegion', types: ['Water', 'Ghost'] },
  { name: 'Sneasler', types: ['Fighting', 'Poison'] },
  // Paldea
  { name: 'Meowscarada', types: ['Grass', 'Dark'] },
  { name: 'Skeledirge', types: ['Fire', 'Ghost'] },
  { name: 'Quaquaval', types: ['Water', 'Fighting'] },
  { name: 'Maushold', types: ['Normal'] },
  { name: 'Garganacl', types: ['Rock'] },
  { name: 'Armarouge', types: ['Fire', 'Psychic'] },
  { name: 'Ceruledge', types: ['Fire', 'Ghost'] },
  { name: 'Bellibolt', types: ['Electric'] },
  { name: 'Scovillain', types: ['Grass', 'Fire'] },
  { name: 'Espathra', types: ['Psychic'] },
  { name: 'Tinkaton', types: ['Fairy', 'Steel'] },
  { name: 'Palafin', types: ['Water'] },
  { name: 'Orthworm', types: ['Steel'] },
  { name: 'Glimmora', types: ['Rock', 'Poison'] },
  { name: 'Farigiraf', types: ['Normal', 'Psychic'] },
  { name: 'Kingambit', types: ['Dark', 'Steel'] },
  { name: 'Sinistcha', types: ['Grass', 'Ghost'] },
  { name: 'Archaludon', types: ['Steel', 'Dragon'] },
  { name: 'Hydrapple', types: ['Grass', 'Dragon'] },
];

// ============================================================
// ITEMS AVAILABLE IN POKEMON CHAMPIONS
// Note: Many traditional competitive items are NOT in this game
// No: Assault Vest, Life Orb, Choice Band, Choice Specs, Rocky Helmet, Loaded Dice
// ============================================================
export const ITEMS_INFO = {
  // Type-boosting items (all 18 types)
  'Black Belt': 'Boosts Fighting-type moves by 20%.',
  'Black Glasses': 'Boosts Dark-type moves by 20%.',
  'Bright Powder': 'Lowers opposing Pokemon accuracy.',
  'Charcoal': 'Boosts Fire-type moves by 20%.',
  'Choice Scarf': 'Boosts Speed by 50% but locks into one move. ONLY Choice item available.',
  'Dragon Fang': 'Boosts Dragon-type moves by 20%.',
  'Fairy Feather': 'Boosts Fairy-type moves by 20%.',
  'Focus Band': 'May survive a KO with 1 HP (chance-based).',
  'Focus Sash': 'Survives any one-hit KO at full HP with 1 HP. Key item — no Life Orb/AV available.',
  'Hard Stone': 'Boosts Rock-type moves by 20%.',
  'King\'s Rock': 'Attacks may cause flinching.',
  'Leftovers': 'Recovers 1/16 max HP each turn.',
  'Light Ball': 'Doubles Pikachu Attack and Sp. Atk.',
  'Magnet': 'Boosts Electric-type moves by 20%.',
  'Mental Herb': 'Cures Taunt, Encore, Disable once.',
  'Metal Coat': 'Boosts Steel-type moves by 20%.',
  'Miracle Seed': 'Boosts Grass-type moves by 20%.',
  'Mystic Water': 'Boosts Water-type moves by 20%.',
  'Never-Melt Ice': 'Boosts Ice-type moves by 20%.',
  'Poison Barb': 'Boosts Poison-type moves by 20%.',
  'Quick Claw': 'May allow moving first occasionally.',
  'Scope Lens': 'Boosts critical-hit ratio.',
  'Sharp Beak': 'Boosts Flying-type moves by 20%.',
  'Shell Bell': 'Restores HP equal to 1/8 of damage dealt.',
  'Silk Scarf': 'Boosts Normal-type moves by 20%.',
  'Silver Powder': 'Boosts Bug-type moves by 20%.',
  'Soft Sand': 'Boosts Ground-type moves by 20%.',
  'Spell Tag': 'Boosts Ghost-type moves by 20%.',
  'Twisted Spoon': 'Boosts Psychic-type moves by 20%.',
  'White Herb': 'Restores any lowered stats once.',
  'Sitrus Berry': 'Restores 25% HP when below 50%.',
  'Lum Berry': 'Cures any status condition once.',
  // Type-resist berries — halve damage from one super-effective hit of the listed type (consumed on use).
  'Occa Berry': 'Halves damage from one super-effective Fire attack (consumed).',
  'Passho Berry': 'Halves damage from one super-effective Water attack (consumed).',
  'Wacan Berry': 'Halves damage from one super-effective Electric attack (consumed).',
  'Rindo Berry': 'Halves damage from one super-effective Grass attack (consumed).',
  'Yache Berry': 'Halves damage from one super-effective Ice attack (consumed).',
  'Chople Berry': 'Halves damage from one super-effective Fighting attack (consumed).',
  'Kebia Berry': 'Halves damage from one super-effective Poison attack (consumed).',
  'Shuca Berry': 'Halves damage from one super-effective Ground attack (consumed).',
  'Coba Berry': 'Halves damage from one super-effective Flying attack (consumed).',
  'Payapa Berry': 'Halves damage from one super-effective Psychic attack (consumed).',
  'Tanga Berry': 'Halves damage from one super-effective Bug attack (consumed).',
  'Charti Berry': 'Halves damage from one super-effective Rock attack (consumed).',
  'Kasib Berry': 'Halves damage from one super-effective Ghost attack (consumed).',
  'Haban Berry': 'Halves damage from one super-effective Dragon attack (consumed).',
  'Colbur Berry': 'Halves damage from one super-effective Dark attack (consumed).',
  'Babiri Berry': 'Halves damage from one super-effective Steel attack (consumed).',
  'Chilan Berry': 'Halves damage from one Normal attack (consumed).',
  'Roseli Berry': 'Halves damage from one super-effective Fairy attack (consumed).',
};

export const ABILITIES_INFO = {
  // Standard abilities
  'Intimidate': 'Lowers all opponents\' Attack by 1 stage on entry.',
  'Drought': 'Sets Sun for 5 turns on entry.',
  'Drizzle': 'Sets Rain for 5 turns on entry.',
  'Sand Stream': 'Sets Sandstorm for 5 turns on entry.',
  'Snow Warning': 'Sets Snow on entry. Boosts Ice Def 50%, Blizzard 100% accuracy.',
  'Chlorophyll': 'Doubles Speed in Sun.',
  'Swift Swim': 'Doubles Speed in Rain.',
  'Sand Rush': 'Doubles Speed in Sandstorm.',
  'Prankster': 'Status moves get +1 priority (doesn\'t affect Dark types).',
  'Competitive': 'SpA rises by 2 stages when any stat is lowered. Hard-counters Intimidate.',
  'Defiant': 'Attack rises by 2 stages when any stat is lowered.',
  'Clear Body': 'Prevents stat reduction from opponents. Ignores Intimidate.',
  'Supreme Overlord': 'Gets +10% Attack/Sp. Atk per fainted teammate.',
  'Magic Bounce': 'Reflects status moves back at the user.',
  'Multiscale': 'Takes 50% damage when at full HP.',
  'Levitate': 'Immune to Ground-type moves.',
  'Armor Tail': 'Blocks ALL priority moves from opponents (Fake Out, Extreme Speed, etc).',
  'Queenly Majesty': 'Blocks ALL priority moves from opponents.',
  'Hospitality': 'Heals partner by 25% max HP on entry.',
  'Bulletproof': 'Immune to ball and bomb moves (Shadow Ball, Aura Sphere, etc).',
  'Technician': 'Moves with 60 or less base power get 50% boost.',
  'Rough Skin': 'Deals 1/8 HP damage to attacker on contact.',
  'Iron Fist': 'Boosts punching moves by 20%.',
  'Mega Launcher': 'Boosts pulse and aura moves by 50%.',
  'Solar Power': 'SpA is 1.5x in Sun, but loses 1/8 HP each turn.',
  'Good as Gold': 'Immune to all status moves.',
  'Regenerator': 'Heals 33% HP on switch-out.',
  'Stamina': 'Raises Defense by 1 stage when hit by an attack.',
  'Adaptability': 'STAB bonus increased from 1.5x to 2x.',
  // New Champions abilities
  'Mega Sol': 'Always acts as if Sun is active. Mega Meganium exclusive.',
  'Dragonize': 'Normal-type moves become Dragon-type with 20% power boost.',
  'Piercing Drill': 'Contact moves hit through Protect, dealing 1/4 damage.',
  'Spicy Spray': 'Burns the attacker when this Pokemon takes damage.',
};
